import apiService from './api.service';
import type {
  AdminTag,
  AdminTrail,
  AdminTrailList,
  AdminLesson,
  AdminLessonQuiz,
  AdminBadge,
  AdminUser,
  AdminUserWrite,
  AdminMission,
  AdminMissionWrite,
  AdminLevel,
  AdminLevelWrite,
  AdminSeason,
  AdminSeasonWrite,
  AdminSector,
  AdminSectorWrite,
  AgentAccessAuditEntry,
  SectorUser,
  SectorTransferPayload,
  SeasonRankingResponse,
  UploadResponse,
  BroadcastSend,
  BroadcastSendResponse,
  BroadcastLog,
  AnalyticsDashboard,
  AnalyticsPeriod,
  CostDashboard,
  CostPeriod,
  CostGranularity,
} from '@/types/admin';

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/** Teto por página aceito pelo backend (`DefaultPagination.max_page_size`). */
const MAX_PAGE_SIZE = 200;
/** Guarda contra laço infinito caso `next` um dia venha sempre preenchido. */
const MAX_PAGES = 50;

class AdminService {
  /**
   * Busca uma listagem inteira, seguindo as páginas até o fim.
   *
   * Toda listagem da API vem paginada em 20. Estes gets liam `data.results` e jogavam
   * fora `count`/`next`, então a tela mostrava a primeira página como se fosse o total,
   * sem aviso nenhum — foi assim que um usuário existente ficou invisível no admin.
   * Para as listas pequenas do admin a tela quer a tabela toda, então o certo é buscar
   * tudo: pede o maior lote que o backend aceita e só volta a pedir se ainda houver
   * `next` (o backend pode baixar o lote pedido, e aí a segunda volta cobre o resto).
   */
  private async fetchAll<T>(url: string, params?: Record<string, unknown>): Promise<T[]> {
    const all: T[] = [];
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      const { data } = await apiService.get<PaginatedResponse<T>>(url, {
        params: { ...params, page, page_size: MAX_PAGE_SIZE },
      });
      all.push(...data.results);
      if (!data.next) break;
    }
    return all;
  }

  // =========================================================================
  // Tags
  // =========================================================================

  async getTags(): Promise<AdminTag[]> {
    return this.fetchAll<AdminTag>('/admin/tags/');
  }

  async createTag(payload: { name: string; color?: string }): Promise<AdminTag> {
    const { data } = await apiService.post<AdminTag>('/admin/tags/', payload);
    return data;
  }

  async updateTag(id: string, payload: Partial<{ name: string; color: string }>): Promise<AdminTag> {
    const { data } = await apiService.patch<AdminTag>(`/admin/tags/${id}/`, payload);
    return data;
  }

  async deleteTag(id: string): Promise<void> {
    await apiService.delete(`/admin/tags/${id}/`);
  }

  // =========================================================================
  // Trails
  // =========================================================================

  async getTrails(params?: { ordering?: string }): Promise<AdminTrailList[]> {
    return this.fetchAll<AdminTrailList>('/admin/trails/', params as Record<string, unknown>);
  }

  async getTrail(id: string): Promise<AdminTrail> {
    const { data } = await apiService.get<AdminTrail>(`/admin/trails/${id}/`);
    return data;
  }

  async createTrail(payload: Partial<AdminTrail> & { tag_ids?: string[] }): Promise<AdminTrail> {
    const { data } = await apiService.post<AdminTrail>('/admin/trails/', payload);
    return data;
  }

  async updateTrail(id: string, payload: Partial<AdminTrail> & { tag_ids?: string[] }): Promise<AdminTrail> {
    const { data } = await apiService.patch<AdminTrail>(`/admin/trails/${id}/`, payload);
    return data;
  }

  async deleteTrail(id: string): Promise<void> {
    await apiService.delete(`/admin/trails/${id}/`);
  }

  async publishTrail(id: string): Promise<void> {
    await apiService.post(`/admin/trails/${id}/publish/`);
  }

  async unpublishTrail(id: string): Promise<void> {
    await apiService.post(`/admin/trails/${id}/unpublish/`);
  }

  // =========================================================================
  // Lessons (nested under trails)
  // =========================================================================

  async getLessons(trailId: string): Promise<AdminLesson[]> {
    const { data } = await apiService.get<AdminLesson[]>(`/admin/trails/${trailId}/lessons/`);
    return data;
  }

  async createLesson(
    trailId: string,
    payload: Partial<AdminLesson>
  ): Promise<AdminLesson> {
    const { data } = await apiService.post<AdminLesson>(
      `/admin/trails/${trailId}/lessons/`,
      payload
    );
    return data;
  }

  async updateLesson(
    trailId: string,
    lessonId: string,
    payload: Partial<AdminLesson>
  ): Promise<AdminLesson> {
    const { data } = await apiService.patch<AdminLesson>(
      `/admin/trails/${trailId}/lessons/${lessonId}/`,
      payload
    );
    return data;
  }

  async deleteLesson(trailId: string, lessonId: string): Promise<void> {
    await apiService.delete(`/admin/trails/${trailId}/lessons/${lessonId}/`);
  }

  async reorderLessons(trailId: string, lessonIds: string[]): Promise<void> {
    await apiService.post(`/admin/trails/${trailId}/lessons/reorder/`, {
      lesson_ids: lessonIds,
    });
  }

  // =========================================================================
  // Badges
  // =========================================================================

  async getBadges(): Promise<AdminBadge[]> {
    return this.fetchAll<AdminBadge>('/admin/badges/');
  }

  async getBadge(id: string): Promise<AdminBadge> {
    const { data } = await apiService.get<AdminBadge>(`/admin/badges/${id}/`);
    return data;
  }

  async createBadge(payload: Partial<AdminBadge>): Promise<AdminBadge & { retroactive_unlocks: number }> {
    const { data } = await apiService.post<AdminBadge & { retroactive_unlocks: number }>(
      '/admin/badges/',
      payload
    );
    return data;
  }

  async updateBadge(id: string, payload: Partial<AdminBadge>): Promise<AdminBadge> {
    const { data } = await apiService.patch<AdminBadge>(`/admin/badges/${id}/`, payload);
    return data;
  }

  async deleteBadge(id: string): Promise<void> {
    await apiService.delete(`/admin/badges/${id}/`);
  }

  // =========================================================================
  // Users
  // =========================================================================

  /**
   * Diferente das listas pequenas, esta devolve o envelope inteiro de propósito: com
   * centenas de usuários a tela pagina de verdade, e para isso precisa de `count`
   * (quantos existem ao todo) e de `next` (se ainda há o que carregar).
   */
  async getUsers(filters?: {
    role?: string;
    is_active?: string;
    search?: string;
    page?: number;
  }): Promise<PaginatedResponse<AdminUser>> {
    const params = new URLSearchParams();
    if (filters?.role) params.set('role', filters.role);
    if (filters?.is_active) params.set('is_active', filters.is_active);
    if (filters?.search) params.set('search', filters.search);
    if (filters?.page) params.set('page', String(filters.page));
    const query = params.toString() ? `?${params.toString()}` : '';
    const { data } = await apiService.get<PaginatedResponse<AdminUser>>(`/admin/users/${query}`);
    return data;
  }

  async getUser(id: string): Promise<AdminUser> {
    const { data } = await apiService.get<AdminUser>(`/admin/users/${id}/`);
    return data;
  }

  async createUser(payload: AdminUserWrite): Promise<AdminUser> {
    const { data } = await apiService.post<AdminUser>('/admin/users/', payload);
    return data;
  }

  async updateUser(id: string, payload: Partial<AdminUserWrite>): Promise<AdminUser> {
    const { data } = await apiService.patch<AdminUser>(`/admin/users/${id}/`, payload);
    return data;
  }

  async resetPassword(id: string, password: string): Promise<void> {
    await apiService.post(`/admin/users/${id}/reset-password/`, { password });
  }

  async toggleActive(id: string): Promise<{ status: string; is_active: boolean }> {
    const { data } = await apiService.post<{ status: string; is_active: boolean }>(
      `/admin/users/${id}/toggle-active/`
    );
    return data;
  }

  // -------------------------------------------------------------------------
  // Gate Hub de Agentes (audit log de mudanças em agent_feature_enabled).
  // -------------------------------------------------------------------------

  async getUserAgentAccessAudit(userId: string): Promise<AgentAccessAuditEntry[]> {
    const { data } = await apiService.get<AgentAccessAuditEntry[]>(
      `/admin/users/${userId}/agent-access-audit/`
    );
    return data;
  }

  async getSectorAgentAccessAudit(sectorId: string): Promise<AgentAccessAuditEntry[]> {
    const { data } = await apiService.get<AgentAccessAuditEntry[]>(
      `/admin/sectors/${sectorId}/agent-access-audit/`
    );
    return data;
  }

  // -------------------------------------------------------------------------
  // Gate do Balanceamento de Linha (audit de `seop_feature_enabled`).
  // Mesmo shape do audit do Hub — tabelas diferentes, resposta idêntica.
  // -------------------------------------------------------------------------

  async getUserSeopAccessAudit(userId: string): Promise<AgentAccessAuditEntry[]> {
    const { data } = await apiService.get<AgentAccessAuditEntry[]>(
      `/admin/users/${userId}/seop-access-audit/`
    );
    return data;
  }

  async getSectorSeopAccessAudit(sectorId: string): Promise<AgentAccessAuditEntry[]> {
    const { data } = await apiService.get<AgentAccessAuditEntry[]>(
      `/admin/sectors/${sectorId}/seop-access-audit/`
    );
    return data;
  }

  // -------------------------------------------------------------------------
  // Gate dos painéis executivos (audit de `dashboard_feature_enabled`).
  // -------------------------------------------------------------------------

  async getUserDashboardAccessAudit(userId: string): Promise<AgentAccessAuditEntry[]> {
    const { data } = await apiService.get<AgentAccessAuditEntry[]>(
      `/admin/users/${userId}/dashboard-access-audit/`
    );
    return data;
  }

  async getSectorDashboardAccessAudit(sectorId: string): Promise<AgentAccessAuditEntry[]> {
    const { data } = await apiService.get<AgentAccessAuditEntry[]>(
      `/admin/sectors/${sectorId}/dashboard-access-audit/`
    );
    return data;
  }

  // =========================================================================
  // Missions
  // =========================================================================

  async getMissions(): Promise<AdminMission[]> {
    return this.fetchAll<AdminMission>('/admin/missions/');
  }

  async getMission(id: string): Promise<AdminMission> {
    const { data } = await apiService.get<AdminMission>(`/admin/missions/${id}/`);
    return data;
  }

  async createMission(payload: AdminMissionWrite): Promise<AdminMission & { auto_assigned_count: number }> {
    const { data } = await apiService.post<AdminMission & { auto_assigned_count: number }>(
      '/admin/missions/',
      payload
    );
    return data;
  }

  async updateMission(id: string, payload: Partial<AdminMissionWrite>): Promise<AdminMission> {
    const { data } = await apiService.patch<AdminMission>(`/admin/missions/${id}/`, payload);
    return data;
  }

  async deleteMission(id: string): Promise<void> {
    await apiService.delete(`/admin/missions/${id}/`);
  }

  // =========================================================================
  // Levels
  // =========================================================================

  async getLevels(): Promise<AdminLevel[]> {
    return this.fetchAll<AdminLevel>('/admin/levels/');
  }

  async getLevel(id: string): Promise<AdminLevel> {
    const { data } = await apiService.get<AdminLevel>(`/admin/levels/${id}/`);
    return data;
  }

  async createLevel(payload: AdminLevelWrite): Promise<AdminLevel> {
    const { data } = await apiService.post<AdminLevel>('/admin/levels/', payload);
    return data;
  }

  async updateLevel(id: string, payload: Partial<AdminLevelWrite>): Promise<AdminLevel> {
    const { data } = await apiService.patch<AdminLevel>(`/admin/levels/${id}/`, payload);
    return data;
  }

  async deleteLevel(id: string): Promise<void> {
    await apiService.delete(`/admin/levels/${id}/`);
  }

  // =========================================================================
  // Lesson Quiz
  // =========================================================================

  async getLessonQuiz(lessonId: string): Promise<AdminLessonQuiz | null> {
    try {
      const { data } = await apiService.get<AdminLessonQuiz>(`/admin/lessons/${lessonId}/quiz/`);
      return data;
    } catch {
      return null;
    }
  }

  async createLessonQuiz(lessonId: string, payload: Omit<AdminLessonQuiz, 'id' | 'lesson_id'>): Promise<AdminLessonQuiz> {
    const { data } = await apiService.post<AdminLessonQuiz>(`/admin/lessons/${lessonId}/quiz/`, payload);
    return data;
  }

  async updateLessonQuiz(lessonId: string, payload: Partial<AdminLessonQuiz>): Promise<AdminLessonQuiz> {
    const { data } = await apiService.patch<AdminLessonQuiz>(`/admin/lessons/${lessonId}/quiz/`, payload);
    return data;
  }

  async deleteLessonQuiz(lessonId: string): Promise<void> {
    await apiService.delete(`/admin/lessons/${lessonId}/quiz/`);
  }

  // =========================================================================
  // File Upload
  // =========================================================================

  async uploadFile(file: File): Promise<UploadResponse> {
    const { data } = await apiService.uploadFile<UploadResponse>('/admin/upload/', file);
    return data;
  }

  // =========================================================================
  // Seasons
  // =========================================================================

  async getSeasons(): Promise<AdminSeason[]> {
    return this.fetchAll<AdminSeason>('/admin/seasons/');
  }

  async getSeason(id: string): Promise<AdminSeason> {
    const { data } = await apiService.get<AdminSeason>(`/admin/seasons/${id}/`);
    return data;
  }

  async createSeason(payload: AdminSeasonWrite): Promise<AdminSeason> {
    const { data } = await apiService.post<AdminSeason>('/admin/seasons/', payload);
    return data;
  }

  async updateSeason(id: string, payload: Partial<AdminSeasonWrite>): Promise<AdminSeason> {
    const { data } = await apiService.patch<AdminSeason>(`/admin/seasons/${id}/`, payload);
    return data;
  }

  async deleteSeason(id: string): Promise<void> {
    await apiService.delete(`/admin/seasons/${id}/`);
  }

  async finalizeSeason(id: string): Promise<{ status: string; snapshot_count: number }> {
    const { data } = await apiService.post<{ status: string; snapshot_count: number }>(
      `/admin/seasons/${id}/finalize/`
    );
    return data;
  }

  async getSeasonRankings(id: string): Promise<SeasonRankingResponse> {
    const { data } = await apiService.get<SeasonRankingResponse>(`/admin/seasons/${id}/rankings/`);
    return data;
  }

  // =========================================================================
  // Broadcast Notifications
  // =========================================================================

  async sendBroadcast(payload: BroadcastSend): Promise<BroadcastSendResponse> {
    const { data } = await apiService.post<BroadcastSendResponse>(
      '/admin/notifications/broadcast/',
      payload
    );
    return data;
  }

  async getBroadcastHistory(): Promise<BroadcastLog[]> {
    const { data } = await apiService.get<BroadcastLog[]>('/admin/notifications/broadcast/history/');
    return data;
  }

  /**
   * O modal de broadcast confirma "enviar para N usuários ativos". Enquanto isto
   * contava `results.length`, o N era o tamanho da página — dizia 20 com 149 ativos.
   */
  async getActiveUsersCount(): Promise<number> {
    const { count } = await this.getUsers({ is_active: 'true' });
    return count;
  }

  // =========================================================================
  // Sectors
  // =========================================================================

  async getSectors(): Promise<AdminSector[]> {
    const { data } = await apiService.get<AdminSector[]>('/admin/sectors/');
    return data;
  }

  async createSector(payload: AdminSectorWrite): Promise<AdminSector> {
    const { data } = await apiService.post<AdminSector>('/admin/sectors/', payload);
    return data;
  }

  async updateSector(id: string, payload: Partial<AdminSectorWrite>): Promise<AdminSector> {
    const { data } = await apiService.patch<AdminSector>(`/admin/sectors/${id}/`, payload);
    return data;
  }

  async deleteSector(id: string): Promise<{ message: string }> {
    const { data } = await apiService.delete<{ message: string }>(`/admin/sectors/${id}/`);
    return data;
  }

  async getSectorUsers(sectorId: string, params?: { search?: string }): Promise<PaginatedResponse<SectorUser>> {
    const results = await this.fetchAll<SectorUser>(
      `/admin/sectors/${sectorId}/users/`,
      params?.search ? { search: params.search } : undefined,
    );
    return { count: results.length, next: null, previous: null, results };
  }

  async getUnassignedUsers(params?: { search?: string }): Promise<PaginatedResponse<SectorUser>> {
    const results = await this.fetchAll<SectorUser>(
      '/admin/sectors/unassigned/users/',
      params?.search ? { search: params.search } : undefined,
    );
    return { count: results.length, next: null, previous: null, results };
  }

  async transferUsers(payload: SectorTransferPayload): Promise<{ message: string; transferred_count: number }> {
    const { data } = await apiService.post<{ message: string; transferred_count: number }>(
      '/admin/sectors/transfer/',
      payload
    );
    return data;
  }

  // =========================================================================
  // Analytics
  // =========================================================================

  async getAnalytics(period: AnalyticsPeriod): Promise<AnalyticsDashboard> {
    const { data } = await apiService.get<AnalyticsDashboard>(`/admin/analytics/?period=${period}`);
    return data;
  }

  async getAgentCosts(period: CostPeriod, granularity: CostGranularity): Promise<CostDashboard> {
    const { data } = await apiService.get<CostDashboard>(
      `/admin/agent-costs/?period=${period}&granularity=${granularity}`,
    );
    return data;
  }
}

export const adminService = new AdminService();
